import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Mail, RefreshCw, Send, Eye, X } from 'lucide-react';
import {
  UniversalPage,
  UniversalLoadingState,
  UniversalEmptyState,
  Card,
  Button,
  PeriodFilter,
  SearchField,
  TABLE_PRESETS,
  NotificationCard,
  StatusBadge,
  Modal,
  ModalBody,
  ModalFooter,
  useToast,
  type StatusTone,
} from '@sighthost/ui';
import { useFilterParams } from '@/shared/hooks/useFilterParams';
import { sastRangeToTimestamps, type ListWindow } from '@/shared/lib/listWindow';
import {
  adminNotificationService,
  type AdminEmailLogRow,
} from '@/domains/notifications/services/adminNotificationService';
import { EdgeFunctionError } from '@/domains/customers/services/adminCustomerService';
import { formatDateTime, relativeTime } from '@/shared/utils/format';
import { ConfirmDialog } from '@/shared/components/feedback/ConfirmDialog';

/**
 * NotificationsLogPage — Sprint Admin-B B-3.
 *
 * Lists every email_log row. Filters by template / status / search / date.
 * v1 preview shows the JSON payload (Q-B4 lock); HTML preview deferred to G6.2.
 * Resend enqueues a new outbox row via notification-resend EF.
 */

const STATUS_TONE: Record<string, StatusTone> = {
  sent: 'active',
  failed: 'overdue',
  failed_terminal: 'overdue',
  pending: 'pending',
};

export default function NotificationsLogPage() {
  const { toast } = useToast();
  const [win, setWin] = useState<ListWindow<AdminEmailLogRow> | null>(null);
  const rows = win?.rows ?? null;
  const [templates, setTemplates] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Filters
  /**
   * A5: this page keeps server-side filtering AND offset pagination. The period
   * is presentation only — `useFilterParams` replaces two bare `<input
   * type="date">` with a labelled control, and still writes to `date_from` /
   * `date_to` exactly as before.
   */
  const filters = useFilterParams({ defaultPeriod: 'all-time' });
  const { start: rangeStart, end: rangeEnd } = filters.range;

  const [templateFilter, setTemplateFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [search, setSearch] = useState('');

  // Preview / resend state
  const [preview, setPreview] = useState<{
    row: AdminEmailLogRow;
    payload: Record<string, unknown> | null;
    loading: boolean;
    /** Set only when the FETCH failed. Distinct from `payload === null`, which
     *  is a fact about the row (no outbox record) — a failure must not be able
     *  to impersonate it. Same distinction as the invoice line-items fix. */
    error?: string;
  } | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  // F-27: outward-facing — this puts a real email in a customer inbox.
  const [resendTarget, setResendTarget] = useState<AdminEmailLogRow | null>(null);

  async function load() {
    setRefreshing(true);
    const bounds = sastRangeToTimestamps(filters.range);
    try {
      const data = await adminNotificationService.list({
        template: templateFilter || undefined,
        status: statusFilter || undefined,
        search: search.trim() || undefined,
        date_from: bounds.since,
        date_to: bounds.until,
      });
      setWin(data);
      setError(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRefreshing(false);
    }
  }

  // Initial load + reload on filter change
  useEffect(() => {
    load();
  }, [templateFilter, statusFilter, rangeStart, rangeEnd]);

  // Search is debounced via a separate effect
  useEffect(() => {
    const id = setTimeout(load, 300);
    return () => clearTimeout(id);
  }, [search]);

  // Load templates once
  useEffect(() => {
    adminNotificationService
      .listTemplates()
      .then(setTemplates)
      .catch(() => { /* non-fatal */ });
  }, []);

  /**
   * F-11. This awaited `getPayload` with no try/catch. On a rejection the
   * `loading: false` line never ran, so the modal sat on its spinner forever
   * with no way out but a page reload — a hang, not an error.
   *
   * The payload stays null on failure and the modal renders the reason; a
   * preview that cannot load is a fact worth stating, not a spinner.
   */
  async function openPreview(row: AdminEmailLogRow) {
    setPreview({ row, payload: null, loading: true });
    try {
      const payload = await adminNotificationService.getPayload(row.id);
      setPreview({ row, payload, loading: false });
    } catch (e: unknown) {
      setPreview({
        row,
        payload: null,
        loading: false,
        error: e instanceof Error ? e.message : String(e),
      });
    }
  }

  async function onResend(row: AdminEmailLogRow) {
    setResendingId(row.id);
    try {
      const result = await adminNotificationService.resend(row.id);
      toast({
        tone: 'success',
        title: result.deduped ? 'Already queued' : 'Queued for next runner tick',
        body: result.deduped
          ? 'A resend was already enqueued in the last 60 seconds. No new outbox row created.'
          : `New outbox row ${result.queued_outbox_id.slice(0, 8)}… runs within ~60s.`,
      });
      // Refresh to surface the new row once the runner sends it.
      setTimeout(load, 5_000);
    } catch (e: unknown) {
      const msg = e instanceof EdgeFunctionError
        ? `${e.message} (${e.code})`
        : e instanceof Error ? e.message : String(e);
      toast({ tone: 'danger', title: 'Could not resend', body: msg });
    } finally {
      setResendingId(null);
    }
  }

  const totalShown = useMemo(() => rows?.length ?? 0, [rows]);

  return (
    <UniversalPage
      title={
        <span className="flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Email notifications
        </span>
      }
      description="Every outbound email. Inspect payloads + resend on demand."
      breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: 'Ops', href: '/admin/ops/audit' },
        { label: 'Notifications' },
      ]}
      actions={
        <Button variant="marketing" size="sm" onClick={load} disabled={refreshing}>
          <RefreshCw className={`mr-1 size-4 ${refreshing ? 'animate-spin' : ''}`} aria-hidden />
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </Button>
      }
    >
      {error && <NotificationCard tone="danger" title="Could not load log" body={error} />}

      <Card interactive={false} className="p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <select
            value={templateFilter}
            onChange={(e) => setTemplateFilter(e.target.value)}
            className="h-9 rounded border border-input bg-card px-3 text-sm"
          >
            <option value="">All templates</option>
            {templates.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-9 rounded border border-input bg-card px-3 text-sm"
          >
            <option value="">Any status</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
            <option value="failed_terminal">Failed (terminal)</option>
            <option value="pending">Pending</option>
          </select>
          <SearchField
            value={search}
            onValueChange={setSearch}
            label="Search email log"
            placeholder="Search recipient or subject…"
          />
          <div className="sm:col-span-2">
            <PeriodFilter
              value={filters.period}
              from={filters.from}
              to={filters.to}
              onChange={filters.setPeriod}
              presets={TABLE_PRESETS}
              variant="menu"
              label="Sent"
            />
          </div>
        </div>

        {/*
          AR-6. 538 rows, 180 per 30 days, page of 50 — about 8.5 days visible.
          Saying so is the difference between "50 emails" and "the 50 most
          recent of five hundred".
        */}
        {win?.truncated && (
          <p className="mt-3 text-xs text-muted-foreground">
            Showing the first {win.limit} — more exist. Narrow the sent period,
            or page through below.
          </p>
        )}
      </Card>

      {!rows && !error && <UniversalLoadingState variant="rows" count={6} />}

      {rows && rows.length === 0 && (
        <Card interactive={false} className="mt-4">
          <UniversalEmptyState
            icon={Mail}
            title="No emails match"
            description="Adjust the filters above or wait for the first outbound email."
          />
        </Card>
      )}

      {rows && rows.length > 0 && (
        <Card interactive={false} className="mt-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-2">When</th>
                  <th className="px-3 py-2">Recipient</th>
                  <th className="px-3 py-2">Template</th>
                  <th className="px-3 py-2">Subject</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Linked</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-border hover:bg-muted/40">
                    <td className="px-4 py-2">
                      <div className="text-foreground" title={formatDateTime(r.created_at)}>
                        {relativeTime(r.created_at)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {formatDateTime(r.created_at)}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-foreground">
                      {r.to_email}
                      {r.parent_email_log_id && (
                        <div className="text-xs text-muted-foreground">↻ resend</div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <code className="rounded-sm bg-card-subtle px-1.5 py-0.5 text-xs">
                        {r.template ?? '—'}
                      </code>
                    </td>
                    <td className="px-3 py-2">
                      <span className="block max-w-[280px] truncate text-foreground" title={r.subject}>
                        {r.subject}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge tone={STATUS_TONE[r.status] ?? 'draft'}>
                        {r.status}
                      </StatusBadge>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {r.related_href ? (
                        <Link to={r.related_href} className="text-primary-ink hover:underline">
                          {r.related_entity_kind} →
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openPreview(r)}
                          aria-label="Preview payload"
                        >
                          <Eye className="size-4" aria-hidden />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setResendTarget(r)}
                          disabled={resendingId === r.id}
                          loading={resendingId === r.id}
                          aria-label="Resend"
                        >
                          <Send className="size-4" aria-hidden />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <footer className="border-t border-border bg-muted px-4 py-2 text-xs text-muted-foreground">
            Showing {totalShown} most recent. HTML preview ships with G6.2 (Resend webhook integration).
          </footer>
        </Card>
      )}

      {preview && (
        <Modal
          open
          onOpenChange={(open) => { if (!open) setPreview(null); }}
          title={preview.row.subject}
          description={
            <>
              To <code className="rounded-sm bg-card-subtle px-1">{preview.row.to_email}</code>{' '}
              · {formatDateTime(preview.row.created_at)}
            </>
          }
        >
          <ModalBody>
            {preview.loading ? (
              <UniversalLoadingState variant="spinner" caption="Loading payload…" />
            ) : preview.error ? (
              <NotificationCard
                tone="danger"
                title="Could not load the payload"
                body={`${preview.error} — this is a load failure, not a missing record. Try again.`}
              />
            ) : preview.payload === null ? (
              <NotificationCard
                tone="info"
                title="Payload not available"
                body="The original notification_outbox row was not found (likely a legacy row). The email_log row itself is shown below."
              />
            ) : (
              <pre className="max-h-[60vh] overflow-auto rounded-sm bg-card-subtle p-3 font-mono text-xs text-foreground">
                {JSON.stringify(preview.payload, null, 2)}
              </pre>
            )}
            <div className="mt-3 rounded-sm border border-info/30 bg-info/5 p-2 text-xs text-muted-foreground">
              v1 shows the JSON payload. HTML preview ships with G6.2 (Resend webhook).
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" onClick={() => setPreview(null)}>
              <X className="size-4" aria-hidden /> Close
            </Button>
            <Button
              onClick={() => setResendTarget(preview.row)}
              disabled={resendingId === preview.row.id}
              loading={resendingId === preview.row.id}
            >
              <Send className="mr-1 size-4" aria-hidden />
              Resend this email
            </Button>
          </ModalFooter>
        </Modal>
      )}

      <ConfirmDialog
        open={resendTarget !== null}
        onOpenChange={(v) => !v && setResendTarget(null)}
        title="Resend this email?"
        description={
          resendTarget
            ? `A fresh copy of "${resendTarget.subject}" is delivered to ${resendTarget.to_email}. They receive it again even if the first one arrived.`
            : undefined
        }
        confirmLabel="Resend email"
        busy={resendingId !== null}
        onConfirm={() => {
          if (resendTarget) {
            const r = resendTarget;
            setResendTarget(null);
            void onResend(r);
          }
        }}
      />
    </UniversalPage>
  );
}
